import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import type { Editor } from "@tiptap/react";
import { SearchInput } from "components";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import type { ChipOption } from "./types";

// ─── Types ───────────────────────────────────────────────────────────

interface ChipInsertMenuProps {
  editor: Editor | null;
  chipOptions: ChipOption[];
  showHtmlInsert?: boolean;
  onInsertHtmlCode?: () => void;
}

// ─── Component ───────────────────────────────────────────────────────

export const ChipInsertMenu = memo(
  ({
    editor,
    chipOptions,
    showHtmlInsert = true,
    onInsertHtmlCode,
  }: ChipInsertMenuProps) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [searchValue, setSearchValue] = useState("");
    const searchInputRef = useRef<HTMLInputElement>(null);
    const isOpen = Boolean(anchorEl);

    const filteredOptions = chipOptions.filter((chip) =>
      chip.label.toLowerCase().includes(searchValue.toLowerCase()),
    );

    useEffect(() => {
      if (isOpen && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [isOpen]);

    const handleOpen = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
      },
      [],
    );

    const handleClose = useCallback(() => {
      setAnchorEl(null);
      setSearchValue("");
    }, []);

    const handleChipInsert = useCallback(
      (chip: ChipOption) => {
        if (!editor) return;

        editor
          .chain()
          .focus()
          .insertVariableChip({
            id: chip.id,
            label: chip.label,
            value: chip.value,
            bgColor: chip.bgColor,
            fontColor: chip.fontColor,
          })
          .run();

        handleClose();
      },
      [editor, handleClose],
    );

    const handleHtmlInsert = useCallback(() => {
      handleClose();
      onInsertHtmlCode?.();
    }, [handleClose, onInsertHtmlCode]);

    const handleSearchKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();
      },
      [],
    );

    return (
      <>
        <IconButton
          size="small"
          aria-label="Insert variable"
          onClick={handleOpen}
          sx={{
            backgroundColor: theme.palette.neutral.main,
            color: theme.palette.lightBg.high,
            "&:hover": { backgroundColor: theme.palette.neutral.main },
            width: "23px",
            height: "23px",
            border: `1px solid ${theme.palette.neutral["010"]}`,
            borderRadius: "6px",
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={isOpen}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          TransitionProps={{
            onEntered: () => {
              if (searchInputRef.current) {
                searchInputRef.current.focus();
              }
            },
          }}
        >
          <Box
            sx={{
              p: "0px 8px",
              position: "sticky",
              top: 0,
              backgroundColor: theme.palette.neutral.main,
              zIndex: 1,
            }}
          >
            <SearchInput
              inputRef={searchInputRef}
              value={searchValue}
              onChange={(e) => {
                e.preventDefault();
                setSearchValue(e.target.value);
              }}
              onKeyDown={handleSearchKeyDown}
              name="chip-search"
              placeholder="Search"
            />
            <Typography
              variant="body-small"
              sx={{ p: "6px 4px 0 4px", color: theme.palette.lightBg.low }}
            >
              Variables
            </Typography>
          </Box>

          <Box sx={{ maxHeight: "300px", overflow: "auto" }}>
            {/* Insert HTML Code option */}
            {showHtmlInsert && (
              <MenuItem sx={{ p: "6px 8px" }} onClick={handleHtmlInsert}>
                <Box
                  sx={{
                    borderRadius: "4px",
                    p: "2px",
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      height: "24px",
                      gap: "8px",
                      border: `1px solid ${theme.palette.neutral["015"]}`,
                      borderRadius: "4px",
                      padding: "2px 6px",
                      backgroundColor: theme.palette.neutral["010"],
                    }}
                  >
                    <Box
                      sx={{
                        width: "18px",
                        height: "18px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: theme.palette.primary.main,
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <title>Insert HTML code</title>
                        <polyline points="16 18 22 12 16 6" />
                        <polyline points="8 6 2 12 8 18" />
                      </svg>
                    </Box>
                    <Typography variant="body-small">
                      Insert HTML code
                    </Typography>
                  </Box>
                </Box>
              </MenuItem>
            )}

            {/* Chip options */}
            {filteredOptions.map((chip) => (
              <MenuItem
                key={chip.id}
                sx={{ p: "6px 8px" }}
                onClick={() => handleChipInsert(chip)}
              >
                <Box
                  sx={{
                    borderRadius: "4px",
                    p: "2px",
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      height: "24px",
                      gap: "8px",
                      border: `1px solid ${theme.palette.neutral["015"]}`,
                      borderRadius: "4px",
                      padding: "2px 6px",
                      backgroundColor: theme.palette.neutral["010"],
                    }}
                  >
                    <Typography variant="body-small">{chip.label}</Typography>
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Box>
        </Menu>
      </>
    );
  },
);

ChipInsertMenu.displayName = "ChipInsertMenu";
